import axiosInstance from '../AxiosInstance';
import axios from 'axios';  // Keep this import for any direct calls

// Define the Course Base URL
const COURSE_BASE_URL = 'https://studentapi.anginatlearning.com/api/course';

const EnrolledCourseService = {
  /**
   * Fetch enrolled courses for the current user
   * @returns {Promise<Object>} Raw API response containing enrolled courses data
   */
  getEnrolledCourses: async () => {
    try {
      // Modified to use axiosInstance with full URL path
      // This ensures token refresh logic will be applied
      const response = await axiosInstance({
        method: 'POST',
        url: `${COURSE_BASE_URL}/courses/enrollment/enrolled-courses`,
        data: {} // Empty object for POST body
      });

      // Check if response has the expected structure
      if (response.data && response.data.status === 'success') {
        console.log('Successfully fetched enrolled courses:', response.data);
        return response.data;
      } else {
        console.error('Unexpected response format:', response.data);
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      console.error('Error fetching enrolled courses:', error);
      // Rest of your error handling remains unchanged
      const token = localStorage.getItem('accessToken');
      console.log("Current token", token);

      if (error.response) {
        console.error('Response error data:', error.response.data);
        console.error('Response status:', error.response.status);
        console.error('Response headers:', error.response.headers);
      } else if (error.request) {
        console.error('Request was made but no response received:', error.request);
      } else {
        console.error('Error message:', error.message);
      }

      throw error;
    }
  },

  /**
   * Mock function to provide sample data when API is not available
   * @returns {Object} Sample enrolled courses data
   */
  getMockEnrolledCourses: () => {
    console.log("Generating mock enrolled courses data");
    
    return {
      status: "success",
      message: "Fetch Enrolled Courses Successfully (Mock Data)",
      data: [
        // Real PMP Course from your API
        {
          _id: "67e5bf13ff0f621e0ed699f4",
          courseId: {
            _id: "67484e5cf99edee89752613a",
            courseName: "PMP-Project Management Professional 7-Dec",
            courseCode: "AL_24_0001_24_0085",
            description: "Project Management course",
            thumbnail: "https://ik.imagekit.io/dgf4txxxo/thumbnails/thumbnail-1734089153060_sm2Bom9fQg.jpeg",
            batches: [
              {
                name: "PMP 1-Apr",
                batchType: "weekdays",
                timeZone: "9:30 AM - 5:30 PM",
                _id: "67dd4f2e1c8cf2e66213a7dc"
              }
            ],
          },
          batchId: "67dd4f2e1c8cf2e66213a7dc",
          status: "active",
          enrolledAt: "2025-03-27T21:11:47.142Z"
        },
        // React Course - sample course 2
        {
          _id: "mock-enrollment-2",
          courseId: {
            _id: "react-course-123",
            courseName: "Advanced React Development",
            description: "Master React with advanced concepts and patterns",
            thumbnail: "https://via.placeholder.com/400x225/61DAFB/000000?text=React+Advanced",
            batches: [
              {
                name: "Weekend Batch",
                batchType: "weekend",
                timeZone: "10:00 AM - 2:00 PM",
                _id: "weekend-batch-456"
              }
            ],
          },
          batchId: "weekend-batch-456",
          status: "active",
          enrolledAt: "2025-03-22T11:00:00.000Z"
        },
        // Data Science Course - sample course 3
        {
          _id: "mock-enrollment-3",
          courseId: {
            _id: "data-science-789",
            courseName: "Data Science Fundamentals",
            description: "Introduction to data science and machine learning",
            thumbnail: "https://via.placeholder.com/400x225/33FF99/000000?text=Data+Science",
            batches: [
              {
                name: "Evening Batch",
                batchType: "weekday",
                timeZone: "6:00 PM - 8:00 PM",
                _id: "evening-batch-789"
              }
            ],
          },
          batchId: "evening-batch-789",
          status: "active",
          enrolledAt: "2025-03-15T09:00:00.000Z"
        }
      ]
    };
  }
};

export default EnrolledCourseService;

/**
 * Service for handling enrolled courses API calls
 * 
 * API Response Structure:
 * {
 *   "status": "success",
 *   "message": "Fetch Enrolled Courses Successfully",
 *   "data": [
 *     {
 *       "_id": "67e5bf13ff0f621e0ed699f4",
 *       "courseId": {
 *         "_id": "67484e5cf99edee89752613a",
 *         "institution": "6726257ac9656e2f6add3c55",
 *         "courseName": "PMP-Project Management Professional 7-Dec",
 *         "courseCode": "AL_24_0001_24_0085",
 *         "description": "Project Management course",
 *         "pricingType": "one-time",
 *         "languages": ["English"],
 *         "batches": [
 *           {
 *             "name": "PMP 1-Apr",
 *             "startDate": "2025-03-31T18:30:00.000Z",
 *             "endDate": "2025-04-03T18:30:00.000Z",
 *             "timeZone": "9:30 AM - 5:30 PM",
 *             "seats": 10,
 *             "status": "active",
 *             "price": [{ "currency": "INR", "offerPrice": 0, "standardPrice": 0 }],
 *             "batchType": "weekdays",
 *             "_id": "67dd4f2e1c8cf2e66213a7dc"
 *           }
 *         ],
 *         "status": "active",
 *         "thumbnail": "https://ik.imagekit.io/dgf4txxxo/thumbnails/thumbnail-1734089153060_sm2Bom9fQg.jpeg"
 *       },
 *       "institutionId": "6726257ac9656e2f6add3c55",
 *       "batchId": "67dd4f2e1c8cf2e66213a7dc",
 *       "userEmail": "tarankhalsa3412@gmail.com",
 *       "status": "active",
 *       "enrolledAt": "2025-03-27T21:11:47.142Z"
 *     }
 *   ]
 * }
 * 
 * Important fields for UI components:
 * - courseId.courseName - Title of the course
 * - courseId.thumbnail - Image URL for the course
 * - courseId.batches[].batchType - Can be used as label
 * - courseId.batches[].name - Can be used as description
 */
